<template>
  <div v-if="showAlertForSmallScreen" class="alert-small-screen">
    <p>This site is only supported on larger screens. Please use a desktop or maximize your browser window.</p>
  </div>
  <div class="faq-container">
    <div class="faq-title">Definitions of Terms Used Throughout This Website</div>
    <ul>
      <li>
        <div class="faq-answer"><b>SNP: </b>Single Nucleotide Polymorphism (SNP). A base pair in the genome where
          individuals differ in the DNA letter (A, C, T, or G).</div>
      </li>

      <li>
        <div class="faq-answer"><b>Allele: </b>A version of a genetic variant. For example, a SNP might have the alleles
          <mark>A</mark> and <mark>C</mark>.
        </div>
      </li>

      <li>
        <div class="faq-answer"><b>Ancestral Allele: </b>A version of a SNP which predates the derived allele.</div>
      </li>

      <li>
        <div class="faq-answer"><b>Derived Allele: </b>A version of a SNP which arose more recently in evolutionary time
          than the ancestral allele.</div>
      </li>

      <li>
        <div class="faq-answer"><b>Z-Score: </b>Quantifies the number of standard deviations away from the mean a
          specific value of a statistic is.</div>
      </li>

      <div class="faq-title">Frequently Asked Questions</div>
    </ul>
    <div class="faq-question">1. I’m interested in the evolution of protein-coding genes, <mark>not Single Nucleotide
        Polymorphisms</mark> (SNPs). Is this web engine irrelevant to my research?</div>
    <div class="faq-answer">
      <ul>
        <li>Not necessarily! Almost all protein-coding genes are physically linked to SNPs, and we scanned for selection
          on a set of SNPs representative of the whole genome.
          So, it is highly likely that the SNPs for which we report selection statistics are physically linked with or
          statistically associated with your protein-coding gene.
          Try navigating to our home page, clicking on a SNP which is physically near the gene in which you’re
          interested, and checking whether the SNP is listed as “tagging” that gene.</li>
      </ul>
    </div>

    <div class="faq-question">2. What are the <mark>WHG</mark>, <mark>EEF</mark>, and <mark>Steppe</mark> populations
      included in the plots of allele frequency trajectories?</div>
    <div class="faq-answer">
      <ul>
        <li>Sometimes, the frequency of an allele might change not because the allele influences evolutionary fitness,
          but instead because populations with different frequencies of the derived allele mixed with each other.
          WHG, EEF, and Steppe are three populations which “mixed into” our sampled population of ancient skeletons at
          specific dates.
          <br>
          <br>
          Frequencies of derived alleles may differ between populations due to random chance (aka “genetic drift”).
          If one of these populations, population 1, rapidly mixed into another population, population 2, then the
          allele frequency in population 2 may abruptly change at the date of contact with population 1.
          In absence of sufficient controls, this abrupt change in population 2 will be detected as “selection” by
          statistical models.<br><br>
          Our statistical model controls for these allele frequency changes due to population mixture. However, as an
          additional safeguard against misinterpretations of allele frequency trajectories,
          we have included the frequency of each SNP’s derived allele in the source population at the date during which
          the population mixture occurred.
          <br><br>For more information about the history WHG, EEF, and Steppe, see [4].
        </li>

      </ul>
    </div>

    <div class="faq-question">3. Which version of the human reference genome do the SNP RSIDs reference?</div>
    <div class="faq-answer">
      <ul>
        <li>GRCh37/hg19</li>
      </ul>
    </div>

    <div class="faq-title">References</div>
    <ol class="reference">
      <!-- <li>
        Ali Akbari, Alison R. Barton, Steven Gazal, Zheng Li7, Mohammadreza Kariminejad, Annabel Perry, Yating Zeng4, Alissa Mittnik, Nick Patterson, Matthew Mah, Xiang Zhou, Alkes L. Price, Eric S. Lander, Ron Pinhasi, Nadin Rohland1, Swapan Mallick, and David Reich (2024).Pervasive findings of directional selection realize the promise of ancient DNA to elucidate human adaptation. Manuscript in preparation.
      </li> -->
      <li id="ref1">
        Akbari, A. et al. Pervasive findings of directional selection realize the promise of ancient DNA to elucidate
        human adaptation. 2024.09.14.613021 Preprint at <a style="color: #A51C30; text-decoration: none;" target="_blank" href="http://doi.org/10.1101/2024.09.14.613021">doi.org/10.1101/2024.09.14.613021</a> (2024).
      </li>
      <li id="ref2">
        Hujoel, M. L. A., Gazal, S., Loh, P. R., Patterson, N., & Price, A. L. (2020). Liability threshold modeling of
        case-control status and family history of disease increases association power. Nat. Genet., 52(5), 541–547.
        doi:10.1038/s41588-020-0613-6
      </li>
      <li id="ref3">
        Price, A. L., Patterson, N. J., Plenge, R. M., Weinblatt, M. E., Shadick, N. A., & Reich, D. (2006). Principal
        components analysis corrects for stratification in genome-wide association studies. Nat. Genet., 38, 904–909.
      </li>
      <li id="ref4">
        Wolfgang Haak, Iosif Lazaridis, Nick Patterson, Nadin Rohland, Swapan Mallick, Bastien Llamas, Guido Brandt,
        Susanne Nordenfelt, Eadaoin Harney, Kristin Stewardson, Qiaomei Fu, Alissa Mittnik, Eszter Bánffy, Christos
        Economou, Michael Francken, Susanne Friederich, Rafael Garrido Pena, Fredrik Hallgren, Valery Khartanovich,
        Aleksandr Khokhlov, Michael Kunst, Pavel Kuznetsov, Harald Meller, Oleg Mochalov, Vayacheslav Moiseyev, Nicole
        Nicklisch, Sandra L. Pichler, Roberto Risch, Manuel A. Rojo Guerra, Christina Roth, Anna Szécsényi-Nagy, Joachim
        Wahl, Matthias Meyer, Johannes Krause, Dorcas Brown, David Anthony, Alan Cooper, Kurt Werner Alt, and David
        Reich (2015). Massive migration from the steppe was a source for Indo-European languages in Europe. Nat.
        Letters. doi:10.1038/nature14317
      </li>
    </ol>
  </div>

</template>

<script>
import { smallScreenMixin } from '@/mixins/smallScreenMixin';

export default {
  name: "FAQ",
  mixins: [smallScreenMixin],
  methods: {
    scroll(_id, top = 90) {
      const manhattanDiv = document.getElementById(_id);
      if (manhattanDiv) {
        window.scrollTo({
          top: top,
          behavior: 'smooth'
        });

        manhattanDiv.classList.add("highlight");

        setTimeout(() => {
          manhattanDiv.classList.remove("highlight");
        }, 3000);
      }
    }
  },
  mounted() {
    const hashParts = window.location.hash.split("#");
    if (hashParts.length > 1) {
      let part = hashParts[hashParts.length - 1]
      if (part == 'manhattan') {
        this.scroll(part, 90)
      } else if (part == 'pheno_more') {
        this.scroll(part, 800)
      } else if (part == 'info-selection') {
        this.scroll(part, 1300)
      } else if (part == 'info-gwas') {
        this.scroll(part, 1300)
      } else if (part == 'info-selection-gwas') {
        this.scroll(part, 1500)
      }

    }
  }
};
</script>

<style>
.faq-container {
  width: 945px;
  margin: 100px auto;
  text-align: left;
  scroll-behavior: smooth;
  overflow-y: auto;
}

.faq-title {
  font-size: 24px;
  margin-top: 30px;
  scroll-behavior: smooth;
  font-weight: bold;
  margin-bottom: 20px;
}

ol li {
  margin-left: 19px;
  margin-top: 30px;
  text-align: justify;
}

ul li {
  margin-left: 20px;
}


.faq-question {
  font-weight: bold;
  scroll-behavior: smooth;
  margin-top: 20px;
  text-align: justify;
}

.reference {
  line-height: 23px;
}

.faq-answer {
  margin-top: 10px;
  font-weight: normal;
  overflow-y: auto;
  scroll-behavior: smooth;
  line-height: 23px;
}

.faq-answer ul li {
  text-align: justify;
  line-height: 25px;
  ;
}

.alert-small-screen {
  font-size: 1.3rem;
  width: 90vw;
  margin: 0 auto;
  text-align: center;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

mark {
  background-color: #e3e3e0;
  padding: 0 2px;
  border-radius: 4px;
}

.highlight {
  animation: highlightFade 8s ease;
  background-color: #a51c312a;
  border-radius: 4px;
  /* Or any color for highlighting */
}

@keyframes highlightFade {
  0% {
    background-color: #a51c312a;
  }

  100% {
    background-color: transparent;
    color: black;
  }
}
</style>